/**
 * The type of the Redux action which is dispatched when an interpreter request is made.
 */
export const REQUEST_INTERPRETER = 'REQUEST_INTERPRETER';
/**
 * The type of the Redux action which is dispatched when an interpreter request is cancelled.
 */
export const CANCEL_REQUEST = 'CANCEL_REQUEST';
/**
 * The type of the Redux action which is dispatched when a new interpreter request is received.
 */
export const INTERPRETER_REQUEST_RECEIVED = 'INTERPRETER_REQUEST_RECEIVED';
/**
 * The type of the Redux action which is dispatched when an interpreter request is accepted.
 */
export const REQUEST_ACCEPTED = 'REQUEST_ACCEPTED';
/**
 * The type of the Redux action which is dispatched when an interpreter request is declined.
 */
export const REQUEST_DECLINED = 'REQUEST_DECLINED';
/**
 * The type of the Redux action which is dispatched when a match is found.
 */
export const MATCH_FOUND = 'MATCH_FOUND';
/**
 * The type of the Redux action which is dispatched when a meeting is initiated.
 */
export const MEETING_INITIATED = 'MEETING_INITIATED';
/**
 * The type of the Redux action dispatched when an interpreted call ends or is
 * locally reconciled as stale.
 */
export const INTERPRETED_CALL_ENDED = 'INTERPRETED_CALL_ENDED';
/**
 * The type of the Redux action which is dispatched when the queue status is updated.
 */
export const QUEUE_STATUS_UPDATE = 'QUEUE_STATUS_UPDATE';
/**
 * The type of the Redux action which is dispatched when the queue connection status changes.
 */
export const QUEUE_CONNECTION_CHANGED = 'QUEUE_CONNECTION_CHANGED';
/**
 * The type of the Redux action dispatched when the queue service rejects a request.
 */
export const QUEUE_SERVICE_ERROR = 'QUEUE_SERVICE_ERROR';
/**
 * Server-decided features from auth_success, e.g. externalMeetingVrs
 * (vrs-server/ws/auth-handlers.js). Turning one on needs no app rebuild.
 */
export const QUEUE_CAPABILITIES_UPDATED = 'QUEUE_CAPABILITIES_UPDATED';
/**
 * The interpreter for the client's own Zoom/Meet/Teams/Webex meeting was
 * matched, or reported entering / identifying the client in it.
 */
export const EXTERNAL_MEETING_CALL_UPDATED = 'EXTERNAL_MEETING_CALL_UPDATED';
/**
 * The external-meeting call ended.
 */
export const EXTERNAL_MEETING_CALL_ENDED = 'EXTERNAL_MEETING_CALL_ENDED';
/**
 * The interpreter's in-call "Pause after this call" choice changed.
 */
export const SET_PAUSE_AFTER_CALL = 'SET_PAUSE_AFTER_CALL';
/**
 * The interpreter's wrap-up between calls began, changed phase or ended.
 */
export const INTERPRETER_WRAP_UP_UPDATED = 'INTERPRETER_WRAP_UP_UPDATED';
/**
 * The server confirmed this interpreter's queue availability (status_updated).
 */
export const INTERPRETER_AVAILABILITY_CHANGED = 'INTERPRETER_AVAILABILITY_CHANGED';
/**
 * The interpreter's queue session moved to another device (session_superseded),
 * or this device took it back (message undefined clears it).
 */
export const INTERPRETER_SESSION_SUPERSEDED = 'INTERPRETER_SESSION_SUPERSEDED';
/**
 * Malka Terp's break state changed (an HTTP break takes the interpreter out of
 * matching without a status_updated, so the socket state alone cannot say).
 */
export const INTERPRETER_BREAK_CHANGED = 'INTERPRETER_BREAK_CHANGED';
/**
 * Calendar meeting reminders (phase 2): replaced from the API, added from the
 * socket, removed when used or dismissed.
 */
export const MEETING_REMINDERS_SET = 'MEETING_REMINDERS_SET';
export const MEETING_REMINDERS_ADDED = 'MEETING_REMINDERS_ADDED';
export const MEETING_REMINDER_REMOVED = 'MEETING_REMINDER_REMOVED';
/**
 * One live call per account: the client's call is open on another device of
 * the account (call_active_elsewhere / account_call_status), or no longer is
 * (hold undefined). VRS Home and the VRI console show it with "Move call here".
 */
export const ACCOUNT_CALL_ELSEWHERE_CHANGED = 'ACCOUNT_CALL_ELSEWHERE_CHANGED';
/** "Move call here": open the account's call on this device (make-before-break). */
export const MOVE_CALL_HERE = 'MOVE_CALL_HERE';
/** "Resume": go back to the call this device put on hold to answer another. */
export const RESUME_HELD_CALL = 'RESUME_HELD_CALL';
/**
 * End the stored call (ring cancel or call end, the stored call cleared) and
 * leave the room to the caller's own next navigation: no hang-up navigation
 * and no route to Home. "Leave video message" uses it (2026-10-12): the
 * hang-up's navigation raced the VideoMail room's navigation.
 *
 * {
 *     type: CALL_END_BEFORE_NAVIGATION,
 *     reason: string
 * }
 */
export const CALL_END_BEFORE_NAVIGATION = 'CALL_END_BEFORE_NAVIGATION';
/**
 * The interpreter's outside-meeting call (docs/outside-meeting-desk.md,
 * meetingRemote.ts): set or replaced from request_accepted,
 * external_meeting_call and external_meeting_status_ack.
 *
 * {
 *     type: INTERPRETER_MEETING_CALL_UPDATED,
 *     interpreterMeetingCall: InterpreterMeetingCall
 * }
 */
export const INTERPRETER_MEETING_CALL_UPDATED = 'INTERPRETER_MEETING_CALL_UPDATED';
/**
 * The interpreter's outside-meeting call ended (call_ended), or the server
 * does not know it any more. No callId clears any call.
 *
 * {
 *     type: INTERPRETER_MEETING_CALL_ENDED,
 *     callId?: string
 * }
 */
export const INTERPRETER_MEETING_CALL_ENDED = 'INTERPRETER_MEETING_CALL_ENDED';
/**
 * Malka Terp sent a step of the outside-meeting call (external_meeting_status).
 *
 * {
 *     type: INTERPRETER_MEETING_STEP_SENT,
 *     callId: string,
 *     event: 'entered' | 'identified' | 'ended'
 * }
 */
export const INTERPRETER_MEETING_STEP_SENT = 'INTERPRETER_MEETING_STEP_SENT';
/**
 * The server refused the step, or did not answer it in time.
 *
 * {
 *     type: INTERPRETER_MEETING_STEP_REFUSED,
 *     callId: string,
 *     error: string
 * }
 */
export const INTERPRETER_MEETING_STEP_REFUSED = 'INTERPRETER_MEETING_STEP_REFUSED';
/**
 * The interpreter's web console desk connected or disconnected
 * (meeting_desk_status). meetingDesk undefined: not known.
 *
 * {
 *     type: MEETING_DESK_STATUS_CHANGED,
 *     meetingDesk?: { connected: boolean, device: string | null }
 * }
 */
export const MEETING_DESK_STATUS_CHANGED = 'MEETING_DESK_STATUS_CHANGED';
/**
 * From inside a call, back to the held one: the current call goes on hold
 * ("Swap") or ends ("End & go back") (Robert, 2026-10-12).
 */
export const SWITCH_TO_HELD_CALL = 'SWITCH_TO_HELD_CALL';
/** End the call this device holds, without going back to it. */
export const END_HELD_CALL = 'END_HELD_CALL';
/** Merge the call this device holds into the one it is in (P2P and group calls only). */
export const MERGE_HELD_CALL = 'MERGE_HELD_CALL';
/**
 * A refusal's offer was taken (activeCallGate presentCallBusyChoice): Hold
 * and call, End and call, or End it and call. The middleware holds or ends
 * the call in the way, leaves its room, then runs the screen's own place().
 *
 * {
 *     type: START_CALL_AFTER_BUSY,
 *     offer: CallBusyOffer,
 *     openCall?: { callId: string, roomName?: string },
 *     place: Function
 * }
 */
export const START_CALL_AFTER_BUSY = 'START_CALL_AFTER_BUSY';
